import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GET } from '../services/api'; 
import {
    Search, Phone, Mail, MapPin, CheckCircle,
    ArrowLeft, Download, User, MessageCircle,
    Building2, Loader2, X, ChevronRight, Briefcase
} from 'lucide-react';

// Fallback image for Detail View (Large Banner)
const DEFAULT_LARGE_AVATAR = "https://cdn3d.iconscout.com/3d/premium/thumb/man-avatar-6299539-5187871.png";

const MembersList = () => {
    // --- State ---
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");

    const lastRequestId = useRef(0);

    // --- Detail View State ---
    const [selectedBusinessId, setSelectedBusinessId] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // ==========================================
    // 1. FETCH MEMBERS
    // ==========================================
    const fetchMembers = useCallback(async (pageNum, query = "") => {
        setLoading(true);
        const currentRequestId = ++lastRequestId.current;

        try {
            let endpoint = `/app/business-directory?limit=25&page=${pageNum}`;
            if (query && query.trim().length > 0) {
                endpoint += `&search=${encodeURIComponent(query.trim())}`;
            }

            const res = await GET(endpoint);

            if (currentRequestId !== lastRequestId.current) return;

            if (res && res.success) {
                if (pageNum === 1) {
                    setMembers(res.data);
                } else {
                    setMembers(prev => [...prev, ...res.data]);
                }
                setTotalPages(res.pagination?.total_pages || 1);
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        } finally {
            if (currentRequestId === lastRequestId.current) {
                setLoading(false);
            }
        }
    }, []);

    // Search Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            fetchMembers(1, searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery, fetchMembers]);

    const clearSearch = () => setSearchQuery("");

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchMembers(nextPage, searchQuery);
    };

    const handleCardClick = (member) => {
        const bId = member.business_id || member._id;
        setSelectedBusinessId(bId);
    };

    // Helper Functions
    const getInitials = (name) => {
        if (!name) return "MB";
        const parts = name.trim().split(" ");
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const formatAddress = (addr) => {
        if (!addr) return "";
        if (typeof addr === 'string') return addr;
        if (typeof addr === 'object') return addr.address || `${addr.city}, ${addr.state}`;
        return "";
    };

    // Fetch Detail Data
    useEffect(() => {
        if (!selectedBusinessId) return;
        const fetchDetail = async () => {
            setDetailLoading(true);
            setDetailData(null);
            try {
                const res = await GET(`/app/business-directory-detail/${selectedBusinessId}`);
                if (res && res.success) setDetailData(res.data);
            } catch (error) { console.error(error); }
            finally { setDetailLoading(false); }
        };
        fetchDetail();
        window.scrollTo(0, 0);
    }, [selectedBusinessId]);

    // ==========================================
    // 🖼️ COMPONENT: LIST AVATAR
    // ==========================================
    const ListAvatar = ({ src, name }) => {
        const [imgError, setImgError] = useState(false);

        // If no source or error occurred, show initials
        if (!src || imgError) {
            return (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-white shadow-md flex items-center justify-center text-[#1A87CF] font-bold text-lg select-none flex-shrink-0">
                    {getInitials(name)}
                </div>
            );
        }

        return (
            <img
                src={src}
                alt={name}
                onError={() => setImgError(true)}
                className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md bg-slate-50 flex-shrink-0"
            />
        );
    };

    // ==========================================
    // SKELETON LOADERS
    // ==========================================
    const ListSkeleton = () => (
        <div className="bg-white p-4 rounded-3xl border border-slate-50 shadow-sm mb-3">
            <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-slate-100 rounded-full animate-pulse"></div>
                <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-100 rounded w-3/4 animate-pulse"></div>
                    <div className="h-3 bg-slate-100 rounded w-1/2 animate-pulse"></div>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div className="h-9 bg-slate-50 rounded-xl animate-pulse"></div>
                <div className="h-9 bg-slate-50 rounded-xl animate-pulse"></div>
                <div className="h-9 bg-slate-50 rounded-xl animate-pulse"></div>
            </div>
        </div>
    );

    const DetailSkeleton = () => (
        <div className="min-h-screen bg-[#F8FAFC]">
            <div className="h-48 bg-slate-200 animate-pulse w-full rounded-b-[2.5rem]"></div>
            <div className="container mx-auto px-4 -mt-20 flex flex-col items-center">
                <div className="w-36 h-36 bg-slate-300 rounded-full border-4 border-white animate-pulse"></div>
                <div className="h-6 bg-slate-200 rounded w-48 mt-4 animate-pulse"></div>
            </div>
            <div className="container mx-auto px-4 mt-8 space-y-4">
                <div className="h-40 bg-white rounded-3xl animate-pulse"></div>
            </div>
        </div>
    );

    // ==========================================
    // RENDER: DETAIL VIEW
    // ==========================================
    if (selectedBusinessId) {
        if (detailLoading || !detailData) return <DetailSkeleton />;

        return (
            <div className="min-h-screen bg-[#F8FAFC] font-sans pb-32 animate-in fade-in slide-in-from-right-10 duration-300">
                <div className="relative">
                    <div className="absolute top-4 left-4 z-20">
                        <button onClick={() => setSelectedBusinessId(null)} className="w-10 h-10 bg-white/30 backdrop-blur-md border border-white/40 rounded-full flex items-center justify-center text-white hover:bg-white/40 transition-all shadow-lg">
                            <ArrowLeft size={20} />
                        </button>
                    </div>
                    <div className="h-52 w-full bg-gradient-to-br from-[#1A87CF] to-[#0d5c91] relative overflow-hidden rounded-b-[3rem] shadow-lg">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#4FB0F0]/20 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl"></div>
                    </div>
                    <div className="absolute -bottom-16 left-0 right-0 flex justify-center z-10">
                        <div className="relative">
                            <div className="w-32 h-32 rounded-full p-1 bg-white shadow-xl">
                                <img
                                    src={detailData.profile_picture || DEFAULT_LARGE_AVATAR}
                                    alt={detailData.full_name}
                                    onError={(e) => { e.target.src = DEFAULT_LARGE_AVATAR; }}
                                    className="w-full h-full rounded-full object-cover bg-gray-50"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container mx-auto px-5 pt-20">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-slate-800 break-words">{detailData.full_name}</h1>
                        <p className="text-[#1A87CF] font-semibold text-sm mt-1 break-words">{detailData.business_name}</p>
                        <p className="text-slate-400 text-xs mt-1 italic break-words">"{detailData.tagline || 'Business Member'}"</p>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-8">
                        {[
                            { icon: Phone, label: 'Call', color: 'text-blue-600', bg: 'bg-blue-50', link: `tel:${detailData.personal_phone}` },
                            { icon: MessageCircle, label: 'Chat', color: 'text-green-600', bg: 'bg-green-50', link: `https://wa.me/91${detailData.personal_phone}` },
                            { icon: Mail, label: 'Email', color: 'text-orange-500', bg: 'bg-orange-50', link: `mailto:${detailData.business_email}` },
                            { icon: MapPin, label: 'Map', color: 'text-purple-600', bg: 'bg-purple-50', link: `https://www.google.com/maps/search/?api=1&query=${detailData.office_latitude},${detailData.office_longitude}` }
                        ].map((action, idx) => (
                            <a key={idx} href={action.link} target={action.label === 'Map' || action.label === 'Chat' ? "_blank" : "_self"} className="flex flex-col items-center gap-2 group" rel="noreferrer">
                                <div className={`w-12 h-12 rounded-2xl ${action.bg} ${action.color} flex items-center justify-center shadow-sm transition-transform group-hover:scale-110`}>
                                    <action.icon size={20} />
                                </div>
                                <span className="text-[10px] font-medium text-slate-500">{action.label}</span>
                            </a>
                        ))}
                    </div>

                    <div className="space-y-5">
                        <div className="bg-white p-5 rounded-3xl shadow-[0_2px_15px_rgba(0,0,0,0.03)] border border-slate-50">
                            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Briefcase size={16} /> Business Info</h3>
                            <p className="text-slate-600 text-sm leading-relaxed mb-5 bg-slate-50 p-3 rounded-xl border border-slate-100 whitespace-pre-line break-words">
                                {detailData.description || "No description provided."}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <DetailItem label="City" value={detailData.city?.name} />
                                <DetailItem label="Chapter" value={detailData.chapter?.name} />
                                <DetailItem label="Joined" value={formatDate(detailData.joining_date)} />
                                <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <span className="text-[10px] text-slate-400 font-bold uppercase block mb-1">Website</span>
                                    <a href={`https://${detailData.website}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-[#1A87CF] truncate block hover:underline">
                                        {detailData.website || 'N/A'}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-5 rounded-3xl shadow-[0_2px_15px_rgba(0,0,0,0.03)] border border-slate-50">
                            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2"><User size={16} /> Contact Info</h3>
                            <div className="space-y-4">
                                <ContactRow icon={Phone} label="Office Phone" value={detailData.office_phone} href={`tel:${detailData.office_phone}`} />
                                <ContactRow icon={Mail} label="Email Address" value={detailData.business_email} href={`mailto:${detailData.business_email}`} truncate />
                                <ContactRow icon={MapPin} label="Office Address" value={formatAddress(detailData.office_address)} />
                            </div>
                        </div>
                        {/* {detailData.business_card_front && (
                            <div className="bg-white p-5 rounded-3xl shadow-[0_2px_15px_rgba(0,0,0,0.03)] border border-slate-50 text-center">
                                <div className="rounded-xl overflow-hidden shadow-sm border border-slate-100 mb-4">
                                    <img src={detailData.business_card_front} alt="Card" className="w-full h-auto" />
                                </div>
                                <a href={detailData.business_card_front} download className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-xl font-medium text-sm hover:bg-slate-700 transition-colors w-full justify-center">
                                    <Download size={16} /> Download Digital Card
                                </a>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>
        );
    }

    // ==========================================
    // RENDER: LIST VIEW
    // ==========================================
    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans pb-32">
            <div className="bg-white pt-6 pb-6 px-5 rounded-b-[2.5rem] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] mb-6 sticky top-0 z-30">
                <div className="max-w-lg mx-auto">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Directory</h1>
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
                            <span className="text-xs font-bold text-slate-600">{members.length}</span>
                        </div>
                    </div>
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            {loading ? <Loader2 className="h-5 w-5 text-[#1A87CF] animate-spin" /> : <Search className="h-5 w-5 text-slate-400 group-focus-within:text-[#1A87CF] transition-colors" />}
                        </div>
                        <input
                            type="text"
                            placeholder="Search members or business..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="block w-full pl-10 pr-10 py-3.5 bg-slate-50 border-none rounded-2xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1A87CF]/20 focus:bg-white transition-all shadow-inner"
                        />
                        {searchQuery && (
                            <button onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600">
                                <X className="h-5 w-5 bg-slate-200 rounded-full p-1" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-lg">
                <div className="space-y-3">
                    {members.map((member) => (
                        <div
                            key={member.business_id || member._id || Math.random()}
                            onClick={() => handleCardClick(member)}
                            className="bg-white p-4 rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-50 cursor-pointer active:scale-[0.98] transition-all hover:shadow-md group"
                        >
                            <div className="flex items-start gap-3.5 mb-4">
                                <div className="relative flex-shrink-0">
                                    <ListAvatar src={member.profile_picture} name={member.full_name} />
                                    <div className="absolute -bottom-1 -right-1 bg-green-500 border-2 border-white rounded-full p-0.5">
                                        <CheckCircle size={10} className="text-white fill-green-500" />
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0 pt-0.5">
                                    <div className="flex justify-between items-start">
                                        <div className="min-w-0 flex-1 pr-2">
                                            <h3 className="text-[16px] font-bold text-slate-800 truncate leading-tight capitalize group-hover:text-[#1A87CF] transition-colors">
                                                {member.full_name}
                                            </h3>
                                            <p className="text-[#1A87CF] text-xs font-semibold truncate capitalize mt-0.5">
                                                {member.business_name}
                                            </p>
                                        </div>
                                        <ChevronRight size={18} className="text-slate-300 group-hover:text-[#1A87CF] transition-colors flex-shrink-0" />
                                    </div>
                                    <p className="text-slate-400 text-[11px] mt-1 truncate capitalize">
                                        {member.tagline || 'Business Owner'}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <ActionButton href={`tel:${member.mobile_number}`} icon={Phone} label="Call" color="text-blue-600" bg="bg-blue-50 hover:bg-blue-100" />
                                <ActionButton href={`mailto:${member.email}`} icon={Mail} label="Email" color="text-orange-500" bg="bg-orange-50 hover:bg-orange-100" />
                                <ActionButton href={`https://www.google.com/maps/search/?api=1&query=${member.office_address}`} icon={MapPin} label="Map" color="text-purple-600" bg="bg-purple-50 hover:bg-purple-100" target="_blank" />
                            </div>
                        </div>
                    ))}

                    {loading && <><ListSkeleton /><ListSkeleton /><ListSkeleton /></>}
                    {!loading && members.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4"><Search className="w-8 h-8 text-slate-300" /></div>
                            <p className="text-slate-800 font-medium">No members found</p>
                            <p className="text-xs mt-1">Try adjusting your search.</p>
                        </div>
                    )}
                    {!loading && page < totalPages && members.length > 0 && (
                        <div className="pt-4 text-center pb-8">
                            <button onClick={handleLoadMore} className="px-8 py-3 bg-white border border-slate-200 text-slate-600 text-sm font-semibold rounded-full shadow-sm hover:border-[#1A87CF] hover:text-[#1A87CF] transition-all active:scale-95">Load More Members</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Sub Components ---

const DetailItem = ({ label, value }) => (
    <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
        <span className="text-[10px] text-slate-400 font-bold uppercase block mb-1">{label}</span>
        <span className="font-semibold text-slate-800 text-sm block truncate">{value || 'N/A'}</span>
    </div>
);

const ContactRow = ({ icon: Icon, label, value, href, truncate }) => (
    <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 flex-shrink-0">
            <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-slate-400 uppercase">{label}</p>
            {href ? (
                <a href={href} className={`text-sm font-semibold text-slate-800 hover:text-[#1A87CF] transition-colors block ${truncate ? 'truncate' : 'break-all'}`}>
                    {value || 'N/A'}
                </a>
            ) : (
                <p className="text-sm font-semibold text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {value || 'N/A'}
                </p>
            )}
        </div>
    </div>
);

const ActionButton = ({ href, icon: Icon, label, color, bg, target }) => (
    <a href={href} onClick={(e) => e.stopPropagation()} target={target} rel={target === "_blank" ? "noreferrer" : undefined} className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl ${bg} ${color} transition-colors border border-transparent`}>
        <Icon size={14} /> <span className="text-xs font-bold">{label}</span>
    </a>
);

export default MembersList;